import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Spinner } from "reactstrap";
import Moment from "react-moment";
import { viewQuote } from "../services/QuoteServices";
import { toast } from "react-toastify";
import { isLoggedIn, formatAddress } from "../utils";
import { quoteStatusMapping } from "../constants/quoteFilters";

export default function ViewQuote() {
  const { id } = useParams();
  const history = useHistory();
  const [quote, setQuote] = useState(null);
  const [quoteItems, setQuoteItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoggedIn()) {
      history.push("/login");
      return;
    }

    if (id) {
      setLoading(true);
      viewQuote(id)
        .then((res) => {
          console.log("Quote data:", res.data);
          const quoteData = res.data?.quote || res.data;
          const items = res.data?.quoteItems || res.data?.quote_items || [];

          setQuote(quoteData);
          setQuoteItems(items);
        })
        .catch((err) => {
          console.error("Error fetching quote:", err);
          toast.error("Failed to load quote details");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, history]);

  const getStatusBadgeClass = (status) => {
    if (!status) return "badge";
    const statusLower = status.toLowerCase().trim();
    // Check if status exists in quoteStatusMapping
    if (quoteStatusMapping[statusLower]) {
      return `badge badge-${statusLower}`;
    }
    return "badge";
  };

  const getStatusDisplay = (status) => {
    if (!status) return "Unknown";
    const statusLower = status.toLowerCase();
    return quoteStatusMapping[statusLower] || status;
  };

  if (loading) {
    return (
      <div className="view-quote-page">
        <div className="quote-loading-container">
          <Spinner animation="border" />
          <p>Loading quote details...</p>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="view-quote-page">
        <div className="quote-error-container">
          <p>Quote not found</p>
          <button
            className="btn btn-primary rounded"
            onClick={() => history.push("/quote")}
          >
            Back to Quotes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="view-quote-page">
      <div className="view-quote-container">
        {/* Quote details section */}
        <div className="view-quote-header">
          <div className="view-quote-header-item">
            <label>Quote ID</label>
            <div className="view-quote-value">{quote.quote_id}</div>
          </div>
          <div className="view-quote-header-item">
            <label>Date Requested</label>
            <div className="view-quote-value">
              {quote.start_date ? (
                <Moment format="MM/DD/YYYY">{quote.start_date}</Moment>
              ) : (
                "N/A"
              )}
            </div>
          </div>
          <div className="view-quote-header-item">
            <label>Expiry Date</label>
            <div className="view-quote-value">
              {quote.expiry_date ? (
                <Moment format="MM/DD/YYYY">{quote.expiry_date}</Moment>
              ) : (
                "N/A"
              )}
            </div>
          </div>
          <div className="view-quote-header-item">
            <label>Requested Delivery Date</label>
            <div className="view-quote-value">
              {quote.request_delivery_date ? (
                <Moment format="MM/DD/YYYY">
                  {quote.request_delivery_date}
                </Moment>
              ) : (
                "N/A"
              )}
            </div>
          </div>
          <div className="view-quote-header-item">
            <label>Status</label>
            <div className="view-quote-value">
              <span className={getStatusBadgeClass(quote.status)}>
                {getStatusDisplay(quote.status)}
              </span>
            </div>
          </div>
          <div className="view-quote-header-item">
            <label>Number of Items</label>
            <div className="view-quote-value">
              {quoteItems.length || quote.item_count || 0}
            </div>
          </div>
          <div className="view-quote-header-item address">
            <label>Shipping Address</label>
            <div className="view-quote-value">
              {formatAddress(
                quote.street,
                quote.city,
                quote.state,
                quote.zip_code
              )}
            </div>
          </div>
        </div>

        {/* Quote items section */}
        <div className="view-quote-items-section">
          <h3 className="view-quote-items-title">Quote Items</h3>
          <div className="view-quote-table-container">
            <table className="view-quote-table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>ASIN</th>
                  <th>Quantity</th>
                  <th>Delivery Notes</th>
                  <th>Unit Price & Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {quoteItems.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="view-quote-empty">
                      No items found in this quote
                    </td>
                  </tr>
                ) : (
                  quoteItems.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="view-quote-product-name">
                        {item.productName || item.product_name || "N/A"}
                      </td>
                      <td className="view-quote-sku-asin">
                        {item.asin ? <div>{item.asin}</div> : "—"}
                      </td>
                      <td className="view-quote-quantity">
                        {item.quantity || 1}
                      </td>
                      <td className="view-quote-notes">
                        {item.notes ? <div>{item.notes}</div> : "—"}
                      </td>
                      <td className="view-quote-price">
                        {item.unit_price || item.subtotal ? (
                          <div>
                            {item.unit_price && (
                              <div className="view-quote-subtotal">
                                ${parseFloat(item.unit_price).toFixed(2)} each
                              </div>
                            )}
                            {item.line_total && (
                              <strong>
                                ${parseFloat(item.line_total).toFixed(2)}
                              </strong>
                            )}
                          </div>
                        ) : (
                          <span className="view-quote-price-pending">
                            Price pending
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Total cost section */}
        <div className="view-quote-total-cost-section">
          <div className="view-quote-total-cost-item">
            <h3 className="view-quote-total-cost-title">Total Tax</h3>
            <div className="view-quote-total-cost-value">${quote.tax || 0}</div>
          </div>
          <div className="view-quote-total-cost-item">
            <h3 className="view-quote-total-cost-title">Total Shipping</h3>
            <div className="view-quote-total-cost-value">
              ${quote.shipping || 0}
            </div>
          </div>
          <hr />
          <div className="view-quote-total-cost-item">
            <h3 className="view-quote-total-cost-title">Total Cost</h3>
            <div className="view-quote-total-cost-value">
              ${quote.totalAmount || 0}
            </div>
          </div>
        </div>

        {/* Quote actions section */}
        <div className="view-quote-actions">
          <button
            className="btn btn-outlined"
            onClick={() => history.push("/quote")}
          >
            Back to Quotes
          </button>
          {quote.status?.toLowerCase() === "draft" && (
            <button
              className="btn btn-edit"
              onClick={() => history.push(`/quote/edit/${id}`)}
            >
              Edit Quote
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
